/* eslint-disable new-cap */
import { BlueprintInstanceType } from '../shared.enums';
import { Connections } from '../connections/connections.class';
import { Countertop } from '../countertops/countertop.class';
import { islandData } from './island.data';
import { IslandType } from './enums';
import { Panel } from '../panels/panel.class';
import { Panels } from '../panels/panels.class';
import { PanelTrims } from '../panel-trims/panel-trims.class';
import { Publish } from '../../decorators';
import { SurroundMaterial } from '../surround-material/surround-material.class';

import { Axis, Vertex3 } from '../../spatial-classes';
import { getBaseMsrpCfn, getCenterCfn, getCenterOffsetsCfn, getIslandDescriptionCfn, getIslandSizeCfn, getIslandSkuCfn, getItemizedMsrpCfn, getOverallSizeCfn, getSkuSizeDetailsCfn, getThicknessForRoleCfn, getTotalMsrpCfn, isCornerIslandCfn, isGrillIslandCfn, isLeftOverlapCfn, isRightOverlapCfn, rotateIslandCfn, setPanelThicknessConfigCfn, setSizeCornerPanelsCfn, setSizeStandardPanelsCfn } from './functions';
import { KitchenDesignBase, KitchenDesignOptions } from '../kitchen-design-base';

import type { ReferenceMap } from '../shared.types';
import type { CornerSize, CornerSizeIn, ExtendedPanelRole, IslandAssetProperties, IslandDataIn, IslandDataOut, ItemizedIslandMsrp, PanelThicknessConfig } from './types';
import type { Size, SizeIn } from '../../types';

export class Island extends KitchenDesignBase<IslandDataIn> {
	#size: Size | CornerSize;
	// sku is generated based on size and type, persisted for reference
	#sku: string;
	// island dimensions can't be edited
	#sizeLocked: boolean;
	// island axis references island orientation
	// surround override
	#surround: SurroundMaterial | null;
	// per-panel thickness overrides
	#panelThickness: PanelThicknessConfig | null;
	// dirty update tracking
	#spatialDirty: boolean;
	// cache
	#getCenterOffsetsCfn
	// axis
	#axis: Axis;
	// runtime-only minimum size metadata (not serialized)
	#minimumSize: Size | null;

	// implemented island type
	type: IslandType;
	// island panel trim instance
	readonly panelTrims: PanelTrims;
	// island connections instance
	readonly connections: Connections;
	// island panels instance
	readonly panels: Panels;

	constructor (options:KitchenDesignOptions<IslandDataIn> = {}) {
		super(BlueprintInstanceType.Island, options);

		const data = islandData(options.dataIn)

		this._id = data.id;
		this.#sku = data.sku;

		// initialize as false
		this.#sizeLocked = false;

		this.#axis = new Axis({ dataIn: data.axis });

		this.type = data.type;
		this.#size = this.type === IslandType.Corner
			? { height: 0, width: 0, depth: { left: 0, right: 0 }}
			: { height: 0, width: 0, depth: 0 };

		this.connections = new Connections({ dataIn: data.connections, parent: this });
		this.panels = new Panels({ type: data.type, dataIn: data.panels, parent: this });
		this.panelTrims = new PanelTrims({ dataIn: data.trims, parent: this });
		this.#surround = data.surround
			? new SurroundMaterial({ dataIn: data.surround, parent: this })
			: null;
		this.#panelThickness = data.panelThickness ?? null;

		// keep this after this.panels.  set.size, sizes the panels
		// console.log('island:constructor:size', data.type, data.size);
		this.size = data.type === IslandType.Corner ? data.size as CornerSize : data.size as Size;

		// default minimum size to current size (runtime-only, not serialized)
		this.#minimumSize = data.type !== IslandType.Corner
			? { ...(this.#size as Size) }
			: null;

		if (this.panels.isNew) this.panelTrims.reset(this.panels);

		this.setInitialized();

		// don't lock until initialized - only Grill, Bar, and Smoker can be resized
		this.#sizeLocked = !(data.type === IslandType.Grill || data.type === IslandType.Bar || data.type === IslandType.Smoker);
		// console.log('island:constructor:serialize', this.serialize(), this.panels.children.map((p) => p.getCornerPoints(false, { translate: false })));
	}

	/**
	 * island origin represents the world space position of the island
	 * For default island: origin = center = 0,0,0 (world space)
	 * For other islands: origin = defaultIsland.origin + this.center
	 * @returns Vertex3 representing the world space position
	 */
	get origin (): Vertex3 {
		const fn = (t: Island): Vertex3 => {
			// Check if this is the default island
			const defaultIsland = t.refMap.islands?.getDefaultIsland();
			if (defaultIsland && t.id === defaultIsland.id) {
				// Default island: origin = center
				return t.center;
			}

			// Other islands: origin = defaultIsland.origin + this.center
			const origin = new Vertex3();
			if (defaultIsland) {
				origin.moveTo(defaultIsland.origin);
				origin.add(new Vertex3({ dataIn: { x: t.center.x, y: 0, z: t.center.z }}));
				origin.y = t.center.y;
			// } else {
			// Fallback to current implementation if no default island
				// const sourceConnections = t.connections.getSourceConnections();
				// if (sourceConnections && sourceConnections.length > 0) {
				// 	const maxHeight = t.refMap.islands.children.reduce((max, obj) => Math.max(max, obj.size.height), 0);
				// 	origin.moveTo(t.center);
				// 	origin.add(sourceConnections[0].targetIsland.origin);
				// 	origin.y = (maxHeight / 2) - (t.size.height / 2);
				// }
			}

			return origin;
		};

		return fn(this);
		// return this.context.cache.match<Vertex3>([this.id, 'origin'], () => ['origin', fn(this)], {
		// 	events: [['island_size', 'updated'], ['connections', 'created'], ['island', 'deleted'], ['connections', 'deleted'], ['island_axis', 'updated']]
		// });
	}

	/**
	 * island position references island orientation and spatial position.
	 * spatial positioning is based on the origin of the kitchen design
	 * @returns Position
	 */
	// get position (): Position {
	// 	const maxHeight = this.refMap.islands.children.reduce((max, obj) => Math.max(max, obj.size.height), 0);
	// 	this.#position.origin.y = (maxHeight / 2) - (this.size.height / 2);

	// 	return this.#position;
	// }

	/**
	 * island dimensions can't be edited
	 */
	get sizeLocked (): boolean { return this.#sizeLocked }

	/**
	 * runtime-only minimum size metadata (not serialized)
	 */
	get minimumSize (): Size | null { return this.#minimumSize }
	set minimumSize (size: Size | null) { this.#minimumSize = size }

	/**
	 * MSRP calculations based on island type and size
	 */
	get msrpBase (): number { return getBaseMsrpCfn(this, this.sku) }
	get msrpTotal (): number { return getTotalMsrpCfn(this) }
	get msrpItemized (): ItemizedIslandMsrp { return getItemizedMsrpCfn(this) }

	/**
	 * Sets the island size, includes updating panel sizes
	 * @emits island_size.updated
	 */
	@Publish('island_size', 'updated')
	set size (size: SizeIn | CornerSizeIn) {
		this.#spatialDirty = true;
		if (this.type === IslandType.Corner) {
			const baseSize = this.#size as CornerSize;
			this.#size = { ...baseSize, ...size, depth: { ...baseSize.depth, ...((size as CornerSizeIn).depth ?? {}) }};
			setSizeCornerPanelsCfn(this);
		} else {
			const baseSize = this.#size as Size;
			if (!this.#sizeLocked) this.#size = { ...baseSize, ...(size as SizeIn) };
			setSizeStandardPanelsCfn(this);
		}
	}
	get size (): Size | CornerSize { return this.#size }

	get hasPassthrough (): boolean { return this.panels.children.some((p) => p.hasPassthrough) }
	get hasVents (): boolean { return this.panels.children.some((p) => p.hasVents) }
	get isCornerIsland (): boolean { return isCornerIslandCfn(this) }

	/**
	 * Returns true if the island is the default island, null if parent is not set
	 * @returns boolean
	 */
	get isDefaultIsland (): boolean {
		if ((this.refMap.islands ?? null) === null) return false;
		return this.id === this.refMap.islands?.getDefaultIsland()?.id;
	}

	get isGrillIsland (): boolean { return isGrillIslandCfn(this) }
	get isLeftOverlap (): boolean { return isLeftOverlapCfn(this) }
	get isRightOverlap (): boolean { return isRightOverlapCfn(this) }

	/**
	 * Number of joins to other islands, excluding attached obstructions (walls, etc.).
	 * Obstructions never make an island undeletable — they auto-delete with it.
	 */
	get islandConnectionCount (): number {
		return this.connections.children.filter((c) => c.targetIsland?.instanceOf === BlueprintInstanceType.Island).length;
	}

	get description (): string { return getIslandDescriptionCfn(this) }
	get linearFeet (): number {
		// fixed-length types must match the length hardcoded into the sku by getIslandSkuCfn
		if (this.type === IslandType.Corner) return 5;
		if (this.type === IslandType.Pizza || this.type === IslandType.Asado) return 3;
		return parseInt(this.skuSize, 10) || 0;
	}
	get overallSize (): Size {
		// return measurePerformance(`overallSize(${this.#sku})`, () => getOverallSizeCfn(this));
		return getOverallSizeCfn(this);
	}
	get surround (): SurroundMaterial | null { return this.#surround ?? this.refMap.kitchen?.material?.surround ?? null; }

	/**
	 * Per-panel thickness override config. null = use surround material defaults.
	 */
	get panelThicknessConfig (): PanelThicknessConfig | null { return this.#panelThickness }

	/**
	 * Sets the panel thickness override config and re-sizes panels.
	 * Adjusts island depth/width so panels grow outward.
	 * @emits panel_thickness.updated
	 */
	@Publish('panel_thickness', 'updated')
	set panelThicknessConfig (config: PanelThicknessConfig | null) { setPanelThicknessConfigCfn({ config, island: this as Island }); }

	/**
	 * Writes the raw panel thickness data. Used by setPanelThicknessConfigCfn.
	 */
	public setPanelThicknessData (config: PanelThicknessConfig | null): void { this.#panelThickness = config; }

	/**
	 * Returns the resolved thickness for a panel role, checking overrides first.
	 * @param role the panel role (front, back, left, right, backLeft, backRight)
	 */
	public getThicknessForRole (role: ExtendedPanelRole): number { return getThicknessForRoleCfn({ role, surround: this.surround, panelThickness: this.#panelThickness }); }

	/**
	 * returns the SKU for the island based on size and type
	 * @return string
	 */
	get sku (): string { return getIslandSkuCfn(this, this.getSkuSizeDetails()) }

	/**
	 * calculates size string value for use in the sku, and pricing countertops
	 * @returns string
	 */
	get skuSize (): string { return this.getSkuSizeDetails()[0] }

	/**
	 * locates and returns a Panel instance for the front panel
	 * @returns Panel
	 */
	get frontPanel (): Panel {
		return this.panels.children.find((p) => p.id === this.panels.frontPanelId) || null;
	}

	/**
	 * Returns height, width and depth for the island as calculated from panels
	 * @returns Size
	 */
	get islandSize (): Size {
		// return measurePerformance(`getIslandSizeCfn(${this.sku})`, () => getIslandSizeCfn(this));
		// return this.context.cache.match<Size>([this.id, 'islandSize'], () => ['islandSize', getIslandSizeCfn(this)], {
		// 	events: [['island_size', 'updated']]
		// });
		return getIslandSizeCfn(this);
	}

	/**
	 * Uniform z-offset for positioning all panels within the island.
	 * Uses the front panel thickness as the reference so all panels shift equally,
	 * maintaining alignment at joints when panels have different thicknesses.
	 */
	get zCenterOffset (): number {
		const { islandSize } = this;
		const frontThickness = this.panels.getFrontPanel().thickness;
		return (islandSize.depth / 2) - (frontThickness / 2);
	}

	/**
	 * island center represents the position relative to the default island
	 * For default island: center = 0,0,0 (relative to itself)
	 * For other islands: center = spatial delta from default island (0,0,0)
	 * The center is calculated based on connections to the source island
	 * @returns Vertex3 representing the center position relative to the default island
	 */
	get center (): Vertex3 {
		// return this.context.cache.match<Vertex3>([this.id, 'center'], () => ['center', getCenterCfn(this)], {
		// 	events: [['island_size', 'updated'], ['connections', 'created'], ['island', 'deleted'], ['connections', 'deleted'], ['island_axis', 'updated']]
		// });
		const center = getCenterCfn(this);
		// console.log('Island.center', this.id, this.#axis.y, center.serialize());
		return center;
	}

	public getCenterOffsets (angle = 0, includeCountertop = false): { front: number; left: number; back: number; right: number; } {
		// return measurePerformance(`getCenterOffsets(${this.sku})`, () => getCenterOffsetsCfn(this, angle, includeCountertop));
		return getCenterOffsetsCfn(this, angle, includeCountertop);
		this.#spatialDirty = false;
	}

	/**
	 * Get and returns the countertop instance for the island
	 * @returns Countertop
	 */
	public getCountertop (): Countertop {
		return (this._refMap?.kitchen?.design?.countertops.filterByIslandId(this.id) ?? [null])[0];
	}

	@Publish('island_axis', 'updated')
	set axis (axis: Axis) {
		this.#spatialDirty = true;
		this.#axis = axis;
	}
	get axis (): Axis { return this.#axis; }

	/**
	 * Rotates the island clockwise, includes updating panel connections
	 * @emits island_position.updated
	 */
	@Publish('island_position', 'updated')
	public rotateClockwise (): void {
		this.#spatialDirty = true;
		rotateIslandCfn(this as Island, true);
	}

	/**
	 * Rotates the island counter-clockwise, includes updating panel connections
	 * @returns void
	 * @emits island_position.updated
	 */
	@Publish('island_position', 'updated')
	public rotateCounterClockwise (): void {
		this.#spatialDirty = true;
		rotateIslandCfn(this as Island, false);
	}

	/**
	 * @param id id to find child reference for
	 * @returns ReferenceMap
	 */
	public getReferenceMap (id: string): ReferenceMap {
		return super.getReferenceMap(id, ['connections', 'panels', 'panelTrims']);
	}

	/**
	 * Sets the countertop overhang for the front panel
	 * @param overhang countertop overhang
	 */
	public setFrontOverhang (overhang: number): void {
		const panel = this.panels.getFrontPanel();
		if (panel) panel.countertopOverhang = overhang;
	}

	/**
	 * Sets the countertop overhang for the left side panel
	 * @param overhang countertop overhang
	 */
	public setLeftOverhang (overhang: number): void {
		const panel = this.panels
			.findById(this.panels.getFrontPanel()?.connections.left.targetPanelId);
		if (panel) panel.countertopOverhang = overhang;
	}

	/**
	 * Sets the countertop overhang for the right side panel
	 * @param overhang countertop overhang
	 */
	public setRightOverhang (overhang: number): void {
		const panel = this.panels
			.findById(this.panels.getFrontPanel()?.connections.right.targetPanelId);
		if (panel) panel.countertopOverhang = overhang;
	}

	/**
	 * Sets the countertop overhang for each back panel
	 * @param overhang countertop overhang
	 */
	public setBackOverhang (overhang: number): void {
		this.panels.children
			.filter((p) => p.isBackPanel)
			.forEach((p) => { p.countertopOverhang = overhang });
	}

	/**
	 * Returns countertop overhangs keyed by cardinal direction.
	 * Corner islands have two back panels ('back left', 'back right') — the
	 * `back` key is set to the maximum of the two so that consumers reading
	 * `overhangs.back` get the correct bounding value.
	 */
	public getOverhangs (): Record<string, number> {
		const overhangs = {
			front: 0,
			right: 0,
			back: 0,
			left: 0,
			rightBack: 0,
			leftBack: 0
		};
		this.panels?.children.forEach((panel) => {
			const { label, countertopOverhang } = panel;
			if (label === 'back left') {
				overhangs.leftBack = countertopOverhang;
				overhangs.back = Math.max(overhangs.back, countertopOverhang);
			} else if (label === 'back right') {
				overhangs.rightBack = countertopOverhang;
				overhangs.back = Math.max(overhangs.back, countertopOverhang);
			} else {
				overhangs[label] = countertopOverhang;
			}
		});
		return overhangs;
	}

	/**
	 * Returns a tuple with sku details based on the size of the island
	 * @returns [string, IslandAssetData]
	 */
	private getSkuSizeDetails (): [string, IslandAssetProperties] {
		return getSkuSizeDetailsCfn(this);
	}

	/**
	 * Serializes the island data
	 * @returns IslandData
	 */
	public serialize (): IslandDataOut {
		return {
			id: this._id,
			sku: this.sku,
			type: this.type,
			size: this.#size as Size | CornerSize,
			// position: this.position.serialize(),
			axis: this.#axis.serialize(),
			origin: this.origin.serialize(),
			center: this.center.serialize(),
			connections: this.connections.serialize(),
			trims: this.panelTrims.serialize(),
			panels: this.panels.serialize(),
			surround: this.#surround?.serialize() ?? null,
			panelThickness: this.#panelThickness
		};
	}

}
